// src/components/main/schedulazione/schedulazioneGanttTypes.ts

// ============================================
// DRAG & DROP TYPE CONSTANTS
// ============================================

/** Drop di un OdP dalla sidebar per creare una nuova frazione */
export const ODP_DROP_TYPE = "SCHED_ODP";

/** Drag di una fase-frazione esistente (spostamento intero) */
export const FASE_BAR_TYPE = "SCHED_FASE_BAR";

/** Resize bordo sinistro di una fase-frazione */
export const FASE_RESIZE_LEFT_TYPE = "SCHED_FASE_RESIZE_LEFT";

/** Resize bordo destro di una fase-frazione */
export const FASE_RESIZE_RIGHT_TYPE = "SCHED_FASE_RESIZE_RIGHT";

/** Drag di una frazione intera (slittamento in blocco) */
export const FRAZIONE_BAR_TYPE = "SCHED_FRAZIONE_BAR";

/** Drop di un nuovo placeholder di disponibilità dalla sidebar */
export const VARIAZIONE_DROP_TYPE = "SCHED_VARIAZIONE";

/** Drag di un placeholder esistente (spostamento intero) */
export const VARIAZIONE_BAR_TYPE = "SCHED_VARIAZIONE_BAR";

/** Resize bordo sinistro di un placeholder */
export const VARIAZIONE_RESIZE_LEFT_TYPE = "SCHED_VARIAZIONE_RESIZE_LEFT";

/** Resize bordo destro di un placeholder */
export const VARIAZIONE_RESIZE_RIGHT_TYPE = "SCHED_VARIAZIONE_RESIZE_RIGHT";

/** Tutti i tipi accettati dalle celle del Gantt */
export const ACCEPTED_DROP_TYPES = [
    ODP_DROP_TYPE,
    FASE_BAR_TYPE,
    FASE_RESIZE_LEFT_TYPE,
    FASE_RESIZE_RIGHT_TYPE,
    FRAZIONE_BAR_TYPE,
    VARIAZIONE_DROP_TYPE,
    VARIAZIONE_BAR_TYPE,
    VARIAZIONE_RESIZE_LEFT_TYPE,
    VARIAZIONE_RESIZE_RIGHT_TYPE,
];

// ============================================
// DRAG ITEM INTERFACES
// ============================================

/** Item trascinato dalla sidebar: un OdP da schedulare */
export interface OdPDragItem {
    type: typeof ODP_DROP_TYPE;
    odpId: number;
    idDocumento?: number;
    codiceArticolo?: string;
    nomeCliente?: string;
    quantitaDaFrazionare?: number;
}

/** Item trascinato: una fase-frazione esistente (move o resize) */
export interface FaseBarDragItem {
    type: typeof FASE_BAR_TYPE | typeof FASE_RESIZE_LEFT_TYPE | typeof FASE_RESIZE_RIGHT_TYPE;
    faseFrazioneId: number;
    frazioneId: number;
    dataDa: string;
    dataA: string;
    resizeEdge?: "left" | "right";
}

/** Item trascinato: una frazione intera (slittamento) */
export interface FrazioneBarDragItem {
    type: typeof FRAZIONE_BAR_TYPE;
    frazioneId: number;
}

/** Item trascinato dalla sidebar: un nuovo placeholder da creare */
export interface VariazioneNewDragItem {
    type: typeof VARIAZIONE_DROP_TYPE;
}

/** Item trascinato: un placeholder esistente (move o resize) */
export interface VariazioneBarDragItem {
    type: typeof VARIAZIONE_BAR_TYPE | typeof VARIAZIONE_RESIZE_LEFT_TYPE | typeof VARIAZIONE_RESIZE_RIGHT_TYPE;
    variazioneId: number;
    dataDa: string;
    dataA: string;
    resizeEdge?: "left" | "right";
}

/** Union type per tutti gli item droppabili */
export type GanttDragItem =
    | OdPDragItem
    | FaseBarDragItem
    | FrazioneBarDragItem
    | VariazioneNewDragItem
    | VariazioneBarDragItem;

// ============================================
// UTILITIES
// ============================================

/**
 * Verifica se il drag item è un OdP dalla sidebar
 */
export const isOdPDragItem = (item: GanttDragItem): item is OdPDragItem => {
    return item.type === ODP_DROP_TYPE;
};

/**
 * Verifica se il drag item è una fase-barra
 */
export const isFaseBarDragItem = (item: GanttDragItem): item is FaseBarDragItem => {
    return (
        item.type === FASE_BAR_TYPE ||
        item.type === FASE_RESIZE_LEFT_TYPE ||
        item.type === FASE_RESIZE_RIGHT_TYPE
    );
};

/**
 * Verifica se il drag item è una frazione-barra (slittamento)
 */
export const isFrazioneBarDragItem = (item: GanttDragItem): item is FrazioneBarDragItem => {
    return item.type === FRAZIONE_BAR_TYPE;
};

/**
 * Verifica se il drag item è un nuovo placeholder dalla sidebar
 */
export const isVariazioneNewDragItem = (item: GanttDragItem): item is VariazioneNewDragItem => {
    return item.type === VARIAZIONE_DROP_TYPE;
};

/**
 * Verifica se il drag item è un placeholder esistente (move o resize)
 */
export const isVariazioneBarDragItem = (item: GanttDragItem): item is VariazioneBarDragItem => {
    return (
        item.type === VARIAZIONE_BAR_TYPE ||
        item.type === VARIAZIONE_RESIZE_LEFT_TYPE ||
        item.type === VARIAZIONE_RESIZE_RIGHT_TYPE
    );
};